import { makeAutoObservable } from 'mobx';
import {successAlert} from 'shared/tools';
import {DomainStore} from 'shared/store';
import {t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import Employee from 'main/stores/employees/Employee';
import {User} from 'main/stores/users';
import {onboarding} from 'main/components/tasks';
import {TaskTemplateForm} from 'main/components/tasks/store';
import {TaskTemplate} from 'main/stores/tasks';
import _ from 'lodash';

class OnboardingContainerState {
  store = new DomainStore();
  config = onboarding;

  modalOpen = false;
  addTaskTemplateModalOpen = false;
  editTaskTemplateModalOpen = false;

  employee;
  allEmployees = [];
  allUsers = [];
  task;
  taskTemplate;
  selectedTaskTemplates = [];
  taskTemplateForm;
  errors = {};

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({parentState}) {
    this.employee = parentState.employee;
  }

  async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL,
      endpoints.USERS.ALL
    ]);
    this.allEmployees = this.store.getAllOrdered(types.EMPLOYEE, Employee);
    this.allUsers = this.store._getAll(types.USER, User);
  }

  openTaskModal(task) {
    this.task = task;
    this.modalOpen = true;
  }

  closeTaskModal() {
    this.modalOpen = false;
  }

  openAddTaskTemplateModal() {
    this.addTaskTemplateModalOpen = true;
  }

  closeAddTaskTemplateModal() {
    this.addTaskTemplateModalOpen = false;
    this.selectedTaskTemplates = [];
  }

  async saveSelectedTaskTemplates() {
    await this.store.post(
      endpoints.TASKS.CREATE_FROM_TEMPLATES,
      null,
      {employee: this.employee, taskTemplates: this.selectedTaskTemplates}
    );

    await this.agent.refresh();
    this.closeAddTaskTemplateModal();
  }

  async uncompleteTask() {
    const {model} = await this.store.patch(
      this.task.link('uncomplete'),
      this.task._type,
      this.task
    );

    if (model) {
      await this.agent.refresh();
      if (model.completed) {
        throw new Error('Failed to uncomplete the task.');
      } else {
        successAlert(t('employees.profile.onboarding_tab.UNCOMPLETED_TASK', {taskName: model.name}));
      }
      this.closeTaskModal();
    }
  }

  async completeTask() {
    const {model} = await this.store.patch(
      this.task.link('complete'),
      this.task._type,
      this.task
    );

    if (model) {
      await this.agent.refresh();
      if (model.completed) {
        successAlert(t('employees.profile.onboarding_tab.COMPLETED_TASK', {taskName: model.name}));
      } else {
        throw new Error('Failed to complete the task.');
      }
      this.closeTaskModal();
    }
  }

  async deleteTask() {
    await this.store.destroy(this.task);
    await this.agent.refresh();

    successAlert(t('employees.profile.onboarding_tab.TASK_DELETED', {taskName: this.task.name}));
    this.closeTaskModal();
  }

  setInteractiveAgent(agent) {
    this.agent = agent;
  }

  openEditTaskTemplateModal(taskTemplate) {
    this.taskTemplate = new TaskTemplate(taskTemplate);
    this.openEditModal();
  }

  openCreateTaskTemplateModal() {
    this.taskTemplate = new TaskTemplate({templateType: this.config.templateType});
    this.openEditModal();
  }

  openEditModal() {
    this.taskTemplateForm = new TaskTemplateForm(this.taskTemplate, this.allEmployees, this.allUsers, this.hasManager);
    this.editTaskTemplateModalOpen = true;
  }

  closeEditTaskTemplateModal() {
    this.editTaskTemplateModalOpen = false;
    this.errors = {};
  }

  saveTaskTemplate() {
    if (!this.validateTaskTemplate()) return null;

    this.selectedTaskTemplates.push(this.taskTemplate);
    this.saveSelectedTaskTemplates();

    this.closeEditTaskTemplateModal();
  }

  validateTaskTemplate() {
    this.errors = this.taskTemplateForm.validate();
    return _.isEmpty(this.errors);
  }

  toggleTaskTemplateSelected(taskTemplate) {
    if (this.taskTemplateSelected(taskTemplate)) {
      _.remove(this.selectedTaskTemplates, { clientId: taskTemplate.clientId });
    } else {
      this.selectedTaskTemplates.push(taskTemplate);
    }
  }

  taskTemplateSelected(taskTemplate) {
    return _.some(this.selectedTaskTemplates, { clientId: taskTemplate.clientId });
  }

  get hasManager() {
    return !!this.employee.managerId;
  }
}

export default OnboardingContainerState;
